import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import styles from "./DropDown.module.css";
import useOnClickOutside from "../hooks/useOnClickOutside";
import useOnResize from "../hooks/useOnResize";
import useOnScrollOutside from "../hooks/useOnScrollOutside";
import { TriggerComponentVariants } from "../select/Select";

/**
 * A dropdown component
 * @param {JSX.Element} triggerComponent - The component that will be used as the trigger for the dropdown and the dropdown's open state control will be passed to the triggerComponent as open and setOpen
 * @param {object} triggerProps - Props to be passed to the triggerComponent
 * @param {JSX.Element} children - The component that will be used as the dropdown content and dropdown's open state control will be passed to the children component as open and setOpen
 * @param {object} childrenProps - Props to be passed to the children component
 * @param {object} style - styles to be passed to the dropdown container can be used to define variables to be applied to the trigger or children component
 * @param {string} position - position of the dropdown content, can be top, bottom, left or right
 * @param {string} animation - animation to be applied to the dropdown content, can be x, xy, y is default
 * @returns {JSX.Element} - A dropdown component
 * @description This component is used to create a dropdown. It takes a triggerComponent and a children component as props. The triggerComponent is the component that will be used as the trigger for the dropdown, and the children component is the component that will be used as the dropdown content. It also takes triggerProps and childrenProps as props, which are used to pass props to the triggerComponent and children component, respectively. The style prop can be used to pass styles to the dropdown container.
 * @example
 * const Select = ({ options = data, active = 1, onOptionSelect }) => {
 * return (
 *    <DropDown
 *        triggerComponent={SelectTrigger}
 *        childrenProps={{ options, active }}
 *    >
 *       {SelectOption}
 *   </DropDown>
 * );
 */
const DropDown = ({
  containerProps,
  triggerComponent: Trigger,
  triggerProps,
  triggerComponentType = TriggerComponentVariants.default,
  children: Children,
  childrenProps,
  style,
  position,
  animation,
  onClose,
  ExtraComponent,
}) => {
  const dropDownRef = useRef(null);
  const [open, setOpen] = useState(false);

  const [optionFilterText, updateOptionFilterText] = useState("");

  const toggle = (e) => {
    setOpen((prevOpen) => !prevOpen);
  };

  useEffect(() => {
    if (!open && onClose) onClose();
  }, [onClose, open]);

  const handleClose = () => {
    setOpen(false);
  };

  useOnClickOutside(dropDownRef, handleClose);

  useOnScrollOutside(dropDownRef, handleClose);

  useOnResize(handleClose);

  // useEffect(()=>{
  //   if(isSearchableMultiSelect && optionFilterText.length>0) {
  //     const newFilteredOptions = childrenProps.options.filter((option) => option.toLowerCase().includes(optionFilterText.toLowerCase()));
  //     setFilteredOptions(newFilteredOptions)
  //   } else {
  //     setFilteredOptions(childrenProps.options)
  //   }
  // }, [optionFilterText, childrenProps.options])

  return (
    <div
      ref={dropDownRef}
      style={{
        ...style,
      }}
      {...containerProps}
      className={[styles.DropDownContainer, containerProps?.className].join(
        " ",
      )}
    >
      <div className={styles.dropDownBtn} onClick={toggle} tabIndex={-1}>
        {ExtraComponent ? <ExtraComponent /> : null}
        <Trigger
          open={open}
          setOpen={setOpen}
          {...triggerProps}
          updateOptionFilterText={updateOptionFilterText}
        />
      </div>

      <DropDownContent
        {...{
          Children,
          childrenProps: { ...childrenProps, optionFilterText },
          position,
          animation,
          open,
          setOpen,
          dropDownRef,
        }}
      />
      {/* )} */}
    </div>
  );
};

const DropDownContent = ({
  Children,
  childrenProps,
  position,
  animation,
  open,
  setOpen,
  dropDownRef,
}) => {
  const getFixedBoundaryElement = (element) => {
    let current = element?.parentElement;
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const hasTransform = style.transform && style.transform !== "none";
      const hasPerspective = style.perspective && style.perspective !== "none";
      const hasFilter = style.filter && style.filter !== "none";
      const hasContain = style.contain && style.contain.includes("paint");
      const hasWillChange =
        style.willChange && style.willChange.includes("transform");

      if (
        hasTransform ||
        hasPerspective ||
        hasFilter ||
        hasContain ||
        hasWillChange
      ) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  };

  const [style, setStyle] = useState({
    top: 0,
    left: 0,
  });

  const contentRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return;

    const dropDown = dropDownRef.current;
    const content = contentRef.current;

    const dropDownRect = dropDown.getBoundingClientRect();
    content.style.overflow = "visible";
    const contentRect = content.getBoundingClientRect();
    content.style.overflow = "hidden";

    const boundaryElement = getFixedBoundaryElement(dropDown);
    const boundaryRect = boundaryElement
      ? boundaryElement.getBoundingClientRect()
      : {
          left: 0,
          top: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        };

    const boundaryWidth = boundaryRect.width || window.innerWidth;
    const boundaryHeight = boundaryRect.height || window.innerHeight;

    const baseLeft = boundaryRect.left;
    const baseTop = boundaryRect.top;

    let x = dropDownRect.left - baseLeft;
    let y = dropDownRect.bottom - baseTop + 8;

    if (y + contentRect.height > boundaryHeight) {
      y = dropDownRect.top - baseTop - contentRect.height - 8;
    }

    if (x + contentRect.width > boundaryWidth) {
      x = boundaryWidth - contentRect.width - 8;
    }

    x = Math.max(8, x);
    y = Math.max(8, y);

    setStyle((prevStyles) => ({
      ...prevStyles,
      width: dropDownRect.width,
      transform: `translate3d(${x}px, ${y}px, 0)`,
    }));
  }, [dropDownRef, open, childrenProps.active, childrenProps.optionFilterText]);

  return (
    <div
      className={[
        styles.dropDownContentWrapper,
        open && styles.show,
        position && styles[position],
        animation && styles[animation],
      ].join(" ")}
      style={style}
    >
      <div ref={contentRef} className={styles.dropDownContent}>
        <Children open={open} setOpen={setOpen} {...childrenProps} />
      </div>
    </div>
  );
};

export default DropDown;
